import React, { useEffect, useMemo, useRef, useState } from "react";
import Icon from "../Shared/Icon";
import KavachJourney from "./KavachJourney";
import KavachInsights from "./KavachInsights";
import KavachPointsSource from "./KavachPointsSource";
import KavachClaimModal from "./KavachClaimModal";
import KavachTransactionsModal from "./KavachTransactionsModal";
import KavachGenerateQrModal from "./KavachGenerateQrModal";
import { Common_Event_Hit } from "../../Actions/UserEvents";
import {
  getKavachSummary,
  getKavachServiceSummary,
  getKavachInsights,
  getUserInfoByRetailerCode,
} from "../../Actions/Kavach";
import {
  KAVACH_COLORS,
  buildKavachViewModel,
  buildPointsSourceViewModel,
  buildInsightsViewModel,
  applyHardcodedClaim,
} from "./KavachData";
import "./Kavach.css";

const getStoredRetailerCode = () => {
  try {
    const userData = JSON.parse(localStorage.getItem("userData"));
    const onBehalfRetCode = localStorage.getItem("onBehalfRetCode");
    const activeRetailerCode =
      onBehalfRetCode &&
      onBehalfRetCode.length > 0 &&
      onBehalfRetCode !== "undefined"
        ? onBehalfRetCode
        : userData?.retailerCode;
    return activeRetailerCode || null;
  } catch {
    return null;
  }
};

const Kavach = () => {
  const [retailerCode] = useState(getStoredRetailerCode);
  const [shopName, setShopName] = useState("");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryTick, setRetryTick] = useState(0);
  const [claimModalOpen, setClaimModalOpen] = useState(false);
  const [transactionsModalOpen, setTransactionsModalOpen] = useState(false);
  const [generateQrModalOpen, setGenerateQrModalOpen] = useState(false);
  const [cbUserId, setCbUserId] = useState(null);
  const [saveoCreditAmount, setSaveoCreditAmount] = useState(0);
  const [kavachCoinCreditAmount, setKavachCoinCreditAmount] = useState(0);
  const journeyRef = useRef(null);

  const [serviceSummary, setServiceSummary] = useState(null);
  const [serviceLoading, setServiceLoading] = useState(true);
  const [serviceError, setServiceError] = useState(null);
  const [serviceRetryTick, setServiceRetryTick] = useState(0);

  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(true);
  const [insightsError, setInsightsError] = useState(null);
  const [insightsRetryTick, setInsightsRetryTick] = useState(0);

  useEffect(() => {
    localStorage.setItem("currentScreen", "kavach_screen");
    Common_Event_Hit("DYNAMIC_EVENT", { eventName: "kavach_screen_view" });
  }, []);

  useEffect(() => {
    if (!retailerCode) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    getKavachSummary(retailerCode)
      .then((res) => setSummary(res.data.data))
      .catch((err) => setError(err))
      .finally(() => setLoading(false));
  }, [retailerCode, retryTick]);

  useEffect(() => {
    if (!retailerCode) return;
    getUserInfoByRetailerCode(retailerCode)
      .then((res) => {
        setCbUserId(res?.data?.data?.cbUserId || null);
        setShopName(res?.data?.data?.shopName || "");
        setSaveoCreditAmount(res?.data?.data?.saveoCreditAmount ?? 0);
        setKavachCoinCreditAmount(res?.data?.data?.kavachCoinCreditAmount ?? 0);
      })
      .catch(() => setCbUserId(null));
  }, [retailerCode]);

  useEffect(() => {
    if (!retailerCode) {
      setServiceLoading(false);
      return;
    }
    setServiceLoading(true);
    setServiceError(null);
    getKavachServiceSummary(retailerCode)
      .then((res) => setServiceSummary(res.data.data))
      .catch((err) => setServiceError(err))
      .finally(() => setServiceLoading(false));
  }, [retailerCode, serviceRetryTick]);

  useEffect(() => {
    if (!retailerCode) {
      setInsightsLoading(false);
      return;
    }
    setInsightsLoading(true);
    setInsightsError(null);
    getKavachInsights(retailerCode)
      .then((res) => setInsights(res.data.data))
      .catch((err) => setInsightsError(err))
      .finally(() => setInsightsLoading(false));
  }, [retailerCode, insightsRetryTick]);

  const insightsViewModel = useMemo(
    () => (insights ? buildInsightsViewModel(insights) : null),
    [insights],
  );

  const viewModel = useMemo(
    () =>
      summary
        ? buildKavachViewModel(
            summary,
            saveoCreditAmount,
            kavachCoinCreditAmount,
          )
        : null,
    [summary, saveoCreditAmount, kavachCoinCreditAmount],
  );

  const pointsSourceViewModel = useMemo(
    () =>
      serviceSummary
        ? buildPointsSourceViewModel(serviceSummary.services)
        : null,
    [serviceSummary],
  );

  const nextMilestone =
    viewModel && viewModel.currentMilestoneIndex >= 0
      ? viewModel.milestones[viewModel.currentMilestoneIndex]
      : null;
  const nextProgressPct = viewModel ? Math.round(viewModel.localFrac * 100) : 0;
  const upcomingMilestone = viewModel?.nextLevel
    ? viewModel.milestones.find(
        (m) => m.step === viewModel.nextLevel.levelNumber,
      )
    : null;

  const retailerName = summary?.retailerName || "";
  const retailerInitials = retailerName
    ? retailerName.trim().slice(0, 2).toUpperCase()
    : "";

  const handleClaimed = () => {
    setSummary((prev) => (prev ? applyHardcodedClaim(prev) : prev));
    setRetryTick((t) => t + 1);
    setInsightsRetryTick((t) => t + 1);
  };

  return (
    <div className="kavach-root" style={styles.page}>
      <div className="kavach-header" style={styles.header}>
        <div className="kavach-header-left" style={styles.headerLeft}>
          <div style={styles.logoBadge}>
            <Icon name="shieldCheck" size={22} color="#fff" />
          </div>
          <div style={styles.logoTextCol}>
            <span className="kavach-logo-text" style={styles.logoText}>
              CureBay Partner Rewards Club
            </span>
            <span className="kavach-logo-tagline" style={styles.logoTagline}>
              Sell More. Earn More. Win More
            </span>
          </div>
        </div>

        <div className="kavach-header-right" style={styles.headerRight}>
          <button
            className="kavach-pressable kavach-generate-qr-btn"
            style={styles.generateQrBtn}
            disabled={!retailerCode}
            onClick={() => setGenerateQrModalOpen(true)}>
            {cbUserId ? "View QR" : "Generate QR"}
          </button>

          <div className="kavach-profile-chip" style={styles.profileChip}>
            <div style={styles.profileAvatar}>{retailerInitials}</div>
            <span className="kavach-profile-name" style={styles.profileName}>
              {retailerName}
            </span>
          </div>
        </div>
      </div>

      <div className="kavach-body" style={styles.body}>
        {!retailerCode && (
          <div style={styles.stateCard}>
            <span style={styles.stateText}>Retailer not available</span>
          </div>
        )}

        {retailerCode && loading && (
          <div style={styles.stateCard}>
            <span style={styles.stateText}>Loading your rewards journey…</span>
          </div>
        )}

        {retailerCode && !loading && error && (
          <div style={styles.stateCard}>
            <span style={styles.stateText}>
              Couldn't load your Kavach rewards right now.
            </span>
            <button
              className="kavach-pressable"
              style={styles.retryBtn}
              onClick={() => setRetryTick((t) => t + 1)}>
              Retry
            </button>
          </div>
        )}

        {retailerCode && !loading && !error && viewModel && (
          <>
            <div className="kavach-left-region" style={styles.leftRegion}>
              <div className="kavach-kpi-row" style={styles.kpiRow}>
                {viewModel.kpiCards.map((kpi) => {
                  const isHistory = kpi.id === "history";
                  return (
                    <div
                      key={kpi.id}
                      style={{
                        ...styles.kpiCard,
                        ...(isHistory ? { cursor: "pointer" } : {}),
                      }}
                      className="kavach-kpi-card kavach-pressable kavach-fade-up"
                      onClick={
                        isHistory
                          ? () => setTransactionsModalOpen(true)
                          : undefined
                      }
                      role={isHistory ? "button" : undefined}>
                      <div style={styles.kpiTopRow}>
                        <span className="kavach-kpi-label" style={styles.kpiLabel}>
                          {kpi.label}
                        </span>
                        <div
                          style={{
                            ...styles.kpiIconWrap,
                            background: `${kpi.accent}1A`,
                          }}>
                          <Icon name={kpi.icon} size={16} color={kpi.accent} />
                        </div>
                      </div>
                      <div style={styles.kpiBody}>
                        {isHistory ? (
                          <div
                            className="kavach-kpi-history"
                            style={styles.kpiHistoryAction}>
                            <span>{kpi.value}</span>
                            <Icon
                              name="chevronRight"
                              size={14}
                              color={kpi.accent}
                            />
                          </div>
                        ) : (
                          <>
                            {!kpi.hideValue && (
                              <div
                                className="kavach-kpi-value"
                                style={styles.kpiValue}>
                                {kpi.value}
                              </div>
                            )}
                            {kpi.sub && (
                              <span
                                className="kavach-kpi-sub"
                                style={styles.kpiSub}>
                                {kpi.sub}
                              </span>
                            )}
                            {kpi.progress !== undefined && (
                              <div style={styles.kpiBarTrack}>
                                <div
                                  style={{
                                    ...styles.kpiBarFill,
                                    width: `${kpi.progress}%`,
                                    background: kpi.accent,
                                  }}
                                />
                              </div>
                            )}
                            {kpi.breakdown && (
                              <div
                                className="kavach-kpi-breakdown"
                                style={styles.kpiBreakdown}>
                                {kpi.breakdown.map((row, index) => (
                                  <React.Fragment key={row.label}>
                                    {index > 0 && (
                                      <div style={styles.kpiBreakdownDivider} />
                                    )}
                                    <div
                                      className="kavach-kpi-breakdown-row"
                                      style={styles.kpiBreakdownRow}>
                                      <span>{row.label}</span>
                                      <span>{row.value}</span>
                                    </div>
                                  </React.Fragment>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="kavach-split-row" style={styles.splitRow}>
                <KavachJourney
                  ref={journeyRef}
                  milestones={viewModel.milestones}
                  localFrac={viewModel.localFrac}
                  overallFrac={viewModel.overallFrac}
                  canClaim={viewModel.canClaim}
                  onClaimClick={() => setClaimModalOpen(true)}
                />

                <div className="kavach-stack-col" style={styles.stackCol}>
                  {viewModel.canClaim && (
                    <div style={styles.nextMilestoneCard}>
                      <div style={styles.nextMilestoneTop}>
                        <span style={styles.nextMilestoneLabel}>
                          NEXT MILESTONE
                        </span>
                        <Icon
                          name="check"
                          size={18}
                          color={KAVACH_COLORS.gold}
                        />
                      </div>

                      <div style={styles.nextMilestoneReward}>
                        Congratulations on reaching Level{" "}
                        {viewModel.currentLevel?.levelNumber}!
                      </div>

                      {viewModel.nextLevel ? (
                        <>
                          <div style={styles.nextMilestoneMeta}>
                            Next milestone:{" "}
                            {upcomingMilestone?.title ||
                              `Level ${viewModel.nextLevel.levelNumber}`}
                          </div>
                          <div style={styles.nextMilestoneRemaining}>
                            <span
                              style={{
                                color: KAVACH_COLORS.gold,
                                fontWeight: 700,
                              }}>
                              {viewModel.nextLevel.pointsToGo.toLocaleString(
                                "en-IN",
                              )}
                            </span>{" "}
                            points to go
                          </div>
                          <div style={styles.goldBarTrack}>
                            <div
                              style={{
                                ...styles.goldBarFill,
                                width: `${nextProgressPct}%`,
                              }}>
                              <div
                                className="kavach-shimmer"
                                style={{ position: "absolute", inset: 0 }}
                              />
                            </div>
                          </div>
                        </>
                      ) : (
                        <div style={styles.nextMilestoneMeta}>
                          You've unlocked every reward. Nice work!
                        </div>
                      )}
                    </div>
                  )}

                  <div style={styles.nextMilestoneCard}>
                    <div style={styles.nextMilestoneTop}>
                      <span style={styles.nextMilestoneLabel}>
                        CLAIM CURRENT LEVEL REWARD
                      </span>
                      <Icon name="gift" size={18} color={KAVACH_COLORS.gold} />
                    </div>

                    {viewModel.canClaim ? (
                      <>
                        <div style={styles.nextMilestoneReward}>
                          {viewModel.claimableOptions
                            .map((o) => o.title)
                            .join(" / ") || "A reward is ready to claim"}
                        </div>
                        <div style={styles.nextMilestoneMeta}>
                          Tap the glowing icon on the path, or claim it here.
                        </div>
                        <div style={styles.ctaBtnRow}>
                          {/* <button
                            className="kavach-pressable"
                            style={styles.moveNextBtn}
                            onClick={() =>
                              journeyRef.current?.scrollToNextLevel()
                            }>
                            Move to Next Level
                            <Icon name="arrowRight" size={13} color="#FFFFFF" />
                          </button> */}
                          <button
                            className="kavach-pressable"
                            style={styles.ctaBtn}
                            onClick={() => setClaimModalOpen(true)}>
                            Claim Reward
                            <Icon
                              name="arrowRight"
                              size={13}
                              color={KAVACH_COLORS.ink}
                            />
                          </button>
                        </div>
                      </>
                    ) : nextMilestone ? (
                      <>
                        {viewModel.currentLevel?.levelNumber ? (
                          <>
                            <div style={styles.nextMilestoneReward}>
                              {nextMilestone.title}
                            </div>
                            <div style={styles.nextMilestoneRemaining}>
                              <span
                                style={{
                                  color: KAVACH_COLORS.gold,
                                  fontWeight: 700,
                                }}>
                                {nextMilestone.remaining.toLocaleString(
                                  "en-IN",
                                )}
                              </span>{" "}
                              points remaining
                            </div>
                          </>
                        ) : (
                          <>
                            <div style={styles.nextMilestoneReward}>
                              Make your first sale to start your journey
                            </div>
                            <div style={styles.nextMilestoneMeta}>
                              {nextMilestone.points.toLocaleString("en-IN")}{" "}
                              points unlocks Level {nextMilestone.step}
                            </div>
                          </>
                        )}
                        <div style={styles.goldBarTrack}>
                          <div
                            style={{
                              ...styles.goldBarFill,
                              width: `${nextProgressPct}%`,
                            }}>
                            <div
                              className="kavach-shimmer"
                              style={{ position: "absolute", inset: 0 }}
                            />
                          </div>
                        </div>
                      </>
                    ) : (
                      <div style={styles.nextMilestoneReward}>
                        You've unlocked every reward. Nice work!
                      </div>
                    )}
                  </div>

                  <KavachInsights
                    insights={insightsViewModel}
                    loading={insightsLoading}
                    error={insightsError}
                    onRetry={() => setInsightsRetryTick((t) => t + 1)}
                    onViewDetails={() => setTransactionsModalOpen(true)}
                  />
                </div>
              </div>
            </div>

            <div className="kavach-sidebar" style={styles.rightSidebar}>
              <KavachPointsSource
                sources={pointsSourceViewModel?.sources || []}
                totalPoints={pointsSourceViewModel?.totalPoints || 0}
                loading={serviceLoading}
                error={serviceError}
                onRetry={() => setServiceRetryTick((t) => t + 1)}
              />
            </div>
          </>
        )}
      </div>

      {viewModel && (
        <KavachClaimModal
          open={claimModalOpen}
          retailerCode={retailerCode}
          levelNumber={viewModel.currentLevel?.levelNumber}
          options={viewModel.claimableOptions}
          onClose={() => setClaimModalOpen(false)}
          onClaim={handleClaimed}
          onMoveNext={() => {
            setClaimModalOpen(false);
            journeyRef.current?.scrollToNextLevel();
          }}
        />
      )}

      <KavachTransactionsModal
        open={transactionsModalOpen}
        retailerCode={retailerCode}
        onClose={() => setTransactionsModalOpen(false)}
      />

      <KavachGenerateQrModal
        open={generateQrModalOpen}
        retailerCode={retailerCode}
        knownCbUserId={cbUserId}
        shopName={shopName}
        onClose={() => setGenerateQrModalOpen(false)}
      />
    </div>
  );
};

const styles = {
  page: {
    background: KAVACH_COLORS.bg,
    minHeight: "100vh",
    paddingTop: "2.5rem",
  },
  header: {
    minHeight: 78,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "14px 32px",
    background: "#FFFFFF",
    borderBottom: "1px solid #E7EAF0",
  },
  headerLeft: { display: "flex", alignItems: "center", gap: 10 },
  logoBadge: {
    width: 38,
    height: 38,
    minWidth: 38,
    borderRadius: 12,
    background: `linear-gradient(150deg, ${KAVACH_COLORS.purple}, ${KAVACH_COLORS.indigo})`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: "0 6px 16px rgba(75,68,149,0.3)",
  },
  logoTextCol: { display: "flex", flexDirection: "column", gap: 2 },
  logoText: {
    fontSize: 16,
    fontWeight: 700,
    letterSpacing: 1.2,
    color: KAVACH_COLORS.ink,
  },
  logoTagline: {
    fontSize: 11.5,
    fontWeight: 500,
    letterSpacing: 0.4,
    color: KAVACH_COLORS.slate,
  },
  headerRight: { display: "flex", alignItems: "center", gap: 14 },
  profileChip: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    border: "1px solid #E2E7EE",
    background: "#F7F9FC",
    borderRadius: 30,
    padding: "6px 12px 6px 6px",
  },
  profileAvatar: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    background: `linear-gradient(150deg, ${KAVACH_COLORS.gold}, ${KAVACH_COLORS.goldDark})`,
    color: "#3A2A05",
    fontSize: 11.5,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  profileName: { fontSize: 13, fontWeight: 600, color: KAVACH_COLORS.ink },
  generateQrBtn: {
    border: "none",
    borderRadius: 30,
    padding: "9px 18px",
    background: KAVACH_COLORS.indigo,
    color: "#FFFFFF",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  body: {
    display: "flex",
    gap: 24,
    padding: "24px 32px 48px",
    alignItems: "flex-start",
  },
  leftRegion: {
    maxWidth: 1220,
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: 20,
  },
  kpiRow: {
    display: "flex",
    gap: 16,
    width: "100%",
  },
  kpiCard: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    background: "#FFFFFF",
    borderRadius: 20,
    padding: "18px 20px",
    boxShadow: "0 6px 20px rgba(75,68,149,0.07)",
  },
  kpiBody: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  kpiTopRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  kpiLabel: {
    fontSize: 12.5,
    fontWeight: 600,
    color: KAVACH_COLORS.slate,
    whiteSpace: "nowrap",
  },
  kpiIconWrap: {
    width: 30,
    height: 30,
    minWidth: 30,
    borderRadius: 9,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  kpiValue: {
    fontSize: 25,
    fontWeight: 700,
    color: KAVACH_COLORS.ink,
    marginTop: 10,
    whiteSpace: "nowrap",
  },
  kpiSub: {
    fontSize: 12,
    color: "#8C9BB5",
    marginTop: 6,
    display: "block",
    whiteSpace: "nowrap",
  },
  kpiBreakdown: {
    marginTop: 8,
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  kpiBreakdownRow: {
    display: "flex",
    justifyContent: "space-between",
    gap: 10,
    fontSize: 11.5,
    color: "#8C9BB5",
    whiteSpace: "nowrap",
  },
  kpiBreakdownDivider: {
    borderTop: "1px solid #EEF1F6",
    margin: "4px 0",
  },
  kpiHistoryAction: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    fontSize: 13.5,
    fontWeight: 600,
    color: KAVACH_COLORS.ink,
    marginTop: 14,
    whiteSpace: "nowrap",
  },
  kpiBarTrack: {
    marginTop: 10,
    height: 7,
    borderRadius: 7,
    background: "#E7EBF3",
    overflow: "hidden",
  },
  kpiBarFill: {
    height: "100%",
    borderRadius: 7,
    transition: "width 900ms cubic-bezier(.22,1,.36,1)",
  },
  stateCard: {
    width: "100%",
    background: "#FFFFFF",
    borderRadius: 20,
    padding: "48px 24px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 14,
    boxShadow: "0 6px 20px rgba(75,68,149,0.07)",
  },
  stateText: { fontSize: 14, fontWeight: 500, color: KAVACH_COLORS.slate },
  retryBtn: {
    border: "none",
    borderRadius: 12,
    padding: "10px 20px",
    background: KAVACH_COLORS.purple,
    color: "#FFFFFF",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  splitRow: { display: "flex", gap: 20, alignItems: "flex-start" },
  stackCol: {
    width: 372,
    minWidth: 372,
    display: "flex",
    flexDirection: "column",
    gap: 18,
  },
  nextMilestoneCard: {
    background: `linear-gradient(160deg, ${KAVACH_COLORS.purpleDark} 0%, ${KAVACH_COLORS.indigo} 55%, #0F1B2E 100%)`,
    borderRadius: 16,
    padding: "16px 16px 18px",
    color: "#fff",
    boxShadow: "0 10px 22px rgba(75,68,149,0.3)",
  },
  nextMilestoneTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  nextMilestoneLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 1,
    color: KAVACH_COLORS.gold,
  },
  nextMilestoneReward: { fontSize: 15.5, fontWeight: 700, marginTop: 6 },
  nextMilestoneMeta: { fontSize: 11.5, color: "#B9C4DE", marginTop: 3 },
  nextMilestoneRemaining: { fontSize: 12, color: "#DCE3F0", marginTop: 8 },
  goldBarTrack: {
    marginTop: 8,
    height: 7,
    borderRadius: 7,
    background: "rgba(255,255,255,0.12)",
    overflow: "hidden",
  },
  goldBarFill: {
    position: "relative",
    height: "100%",
    borderRadius: 7,
    background: `linear-gradient(90deg, ${KAVACH_COLORS.gold}, #FFE9A0)`,
    overflow: "hidden",
    transition: "width 900ms cubic-bezier(.22,1,.36,1)",
  },
  ctaBtnRow: {
    marginTop: 12,
    display: "flex",
    gap: 10,
  },
  ctaBtn: {
    flex: 1,
    minWidth: 0,
    border: "none",
    borderRadius: 12,
    padding: "9px 10px",
    background: `linear-gradient(150deg, ${KAVACH_COLORS.gold}, ${KAVACH_COLORS.goldDark})`,
    color: KAVACH_COLORS.ink,
    fontSize: 11.5,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
    cursor: "pointer",
    boxShadow: "0 8px 20px rgba(255,165,0,0.35)",
  },
  moveNextBtn: {
    flex: 1,
    minWidth: 0,
    border: "1px solid rgba(255,255,255,0.3)",
    borderRadius: 12,
    padding: "9px 10px",
    background: "rgba(255,255,255,0.08)",
    color: "#FFFFFF",
    fontSize: 11.5,
    fontWeight: 700,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 4,
    cursor: "pointer",
  },
  rightSidebar: {
    position: "sticky",
    top: "8rem",
    height: "calc(100vh - 8rem - 24px)",
    display: "flex",
  },
};

export default Kavach;
